/**
 * Prepare an event's subscription CTA from its promoter's / host's organization CTA.
 *
 * Migration 1360 (owner 2026-10-30): the voucher amount defaults from the venue state's default (gun shows capped at
 * $20; $20 when a state has none), and an organization CTA at another amount -- or with no voucher at all -- is used
 * as a TRACKING TEMPLATE when it has an affid and a promo code (only its tracking codes are copied).
 *
 * ⭐ The source rule and the parameter rules live in the DATABASE, in `_eib_event_cta_proposal`
 * (migration 2026-09-26-005). This file only carries the RPC shapes. It deliberately contains no
 * copy of the rule — a TypeScript resolver used to exist (eventCTADefaults.ts) and was removed so the
 * preview the operator sees and the row the server writes can never be computed two different ways.
 *
 * All three RPCs return a `status` instead of raising for a business outcome, so the UI can say
 * WHICH refusal happened.
 */
import { supabase } from "./supabase";
import { ctaActivityLabel } from "../utils/ctaActivityType";

export type CTAInitParameters = Partial<
  Record<
    | "affid"
    | "promo"
    | "altprice"
    | "utm_source"
    | "utm_medium"
    | "utm_campaign"
    | "utm_term"
    | "utm_content",
    string
  >
>;

export interface CTAInitOverride {
  key: string;
  action: "dropped" | "replaced_by_event_state" | "replaced_by_rule";
  reason: string;
  source_value?: string;
  submitted_value?: string;
}

/** What the server proposes. `resolution` is how the source was chosen, or `review:<why>`. */
export interface CTAInitProposal {
  event_id: string;
  event_type: string | null;
  event_state: string | null;
  resolution: string;
  match_scope:
    | "location_matched"
    | "organization_level"
    | "organization_codes"
    | "none";
  /**
   * Migration 440: on 'review:no_cta_at_amount', the distinct voucher amounts the store's CTAs in the
   * matched scope DO have (null = a CTA with no amount). NULL otherwise.
   */
  scope_amounts?: (number | null)[] | null;
  /** Migration 530 (MSR EVT-04): present only when the chosen source CTA is marked for an activity. */
  source_activity_type?: string;
  /**
   * Migration 1360: present only when the source CTA has another amount (or none) and lends its tracking codes only.
   */
  source_role?: "tracking_template";
  /**
   * Migration 1580: present only for a range night in a state with a voucher. 'class' = copied from the host's CLASS
   * promo CTA (promo code ending in CLASS); 'missing' = the host has none, so the older
   * source above was used and its promo code may be outdated. Both set UTM Medium RANGENIGHT (owner 2026-11-03).
   */
  class_promo?: {
    status: "class" | "missing";
    utm_medium?: string | null;
    /** 'missing' only: the host has a CLASS CTA, but only at another of its venues (never used). */
    other_venue_class?: boolean;
  };
  /**
   * Migration 530: on 'review:no_cta_for_activity', the activities the store's CTAs at this amount ARE marked for.
   * Absent otherwise.
   */
  scope_activity_types?: string[];
  /**
   * Migration 1712: present only for a gun show whose promoter has no CTA to copy. 'applied' = the tracking codes come
   * from the state's No Reseller organization (its CTA, or its Affiliate ID / Promo Code); 'unavailable' = the state has
   * no No Reseller organization (or it has no codes), so nothing was copied.
   */
  no_reseller_fallback?: NoResellerFallback;
  source_organization_id: string | null;
  source_organization_name: string | null;
  source: {
    id: string;
    name: string;
    created_at: string;
    updated_at: string;
    location_id: string | null;
    location_name: string | null;
    voucher_amount: number | null;
    parameters: Record<string, string>;
  } | null;
  voucher_amount: number;
  parameters: CTAInitParameters;
  derived_overrides: CTAInitOverride[];
  needs_confirmation: { altprice?: string };
  missing_parameters: string[];
  /** Hash of the INPUTS the proposal was built from; the save refuses if they moved since. */
  fingerprint: string;
}

export interface NoResellerFallback {
  status: "applied" | "unavailable";
  state: string | null;
  mode?: "cta" | "organization_codes";
  organization_id?: string;
  organization_name?: string;
  promoter_organization_id?: string;
  promoter_organization_name?: string | null;
  /** What the promoter alone resolved to (source_missing, review:no_cta_at_amount, ...). */
  promoter_resolution?: string;
  reason?: "no_state_organization" | "no_codes";
}

/** Migration 1712: why a gun show has no tracking codes even with the state No Reseller fallback; "" otherwise. */
export function noResellerUnavailableNote(p: CTAInitProposal): string {
  const f = p.no_reseller_fallback;
  if (!f || f.status !== "unavailable") {
    return "";
  }
  const st = f.state ?? "this state";
  return f.reason === "no_codes"
    ? ` The ${st} No Reseller organization has no Affiliate ID or CTA, so it can't be used instead.`
    : ` ${st} has no No Reseller codes to use instead.`;
}

export interface CTAInitRecord {
  status: "deferred" | "initialized";
  resolution: string | null;
  match_scope: string | null;
  source_cta_id: string | null;
  source_organization_name: string | null;
  source_location_name: string | null;
  derived_overrides: CTAInitOverride[];
  voucher_amount: number | null;
  initialized_at: string | null;
  deferred_at: string | null;
  actor_name: string | null;
  /** The prepared generic CTA's id (NULL once deleted). */
  event_cta_id?: string | null;
  /** An initialized latch whose CTA was later deleted: it stays initialized (at most once per event). */
  cta_deleted?: boolean;
}

export type CTAInitPreviewStatus =
  | "predates_release"
  | "ready"
  | "deferred"
  | "initialized"
  | "not_pristine"
  | "not_eligible"
  | "forbidden"
  | "event_gone";

/** Migration 1360: the event's default voucher amount and where it came from. */
export interface CTADefaultAmount {
  amount: number;
  basis:
    | "event_setting"
    | "state_default"
    | "gun_show_cap"
    | "no_state_default"
    | "fixed"
    | "state_offer"
    | "state_offer_no_voucher"
    | "state_offer_no_offer";
  state: string | null;
  mode?: string;
  /** gun_show_cap only: the state's (retail) default the cap was applied to. */
  state_default?: number;
  /** Mig 1480 (mode state_offers): the state offer's amount -- the most this event's CTA may give. */
  ceiling?: number;
  /** Mig 1480: the state offer includes a Gunshot Wound Kit (Alabama). */
  gsw_kit?: boolean;
}

/** Mig 1480: the most an event's CTA may give (the state offer), or null when no ceiling applies. */
export function ceilingOf(
  d: CTADefaultAmount | null | undefined,
): number | null {
  const c = Number(d?.ceiling);
  return d &&
    d.ceiling !== undefined &&
    d.ceiling !== null &&
    Number.isFinite(c) &&
    c > 0
    ? c
    : null;
}

/** Mig 1480: an event's state offer, for the CTA forms (amount ceiling). */
export interface EventStateOffer {
  status: "ok" | "forbidden" | "event_gone";
  applies?: boolean;
  state?: string | null;
  offer_status?: "voucher" | "no_voucher" | "no_offer" | null;
  ceiling?: number | null;
  gsw_kit?: boolean;
  insurance_state?: boolean;
}

export async function getEventStateOffer(
  eventId: string,
): Promise<EventStateOffer> {
  const { data, error } = await supabase.rpc("event_state_offer", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return (data ?? { status: "event_gone" }) as EventStateOffer;
}

/**
 * Mig 1480 ceiling check for a form: null when the amount is allowed, else the operator-facing reason. `current` is the
 * CTA's saved amount (an edit that does not raise it is always allowed, as on the server).
 */
export function ceilingProblem(
  offer: EventStateOffer | null | undefined,
  amount: number,
  current?: number | null,
): string | null {
  const c = offer?.offer_status === "voucher" ? Number(offer.ceiling) : NaN;
  if (!Number.isFinite(c) || c <= 0 || !(amount > c)) {
    return null;
  }
  if (
    current !== null &&
    current !== undefined &&
    Number.isFinite(Number(current)) &&
    amount <= Number(current)
  ) {
    return null;
  }
  return `The most this event's CTA can give is $${formatAmount(c)}: that is the Event in a Box offer for ${offer?.state ?? "this state"}. You can lower the amount, not raise it above that.`;
}

/** Migration 1580: a range night in a voucher state -- is the event CTA's promo code a CLASS code? */
export interface CTAClassPromoStatus {
  applies: true;
  organization_id: string | null;
  organization_name: string | null;
  cta_promo: string | null;
  cta_promo_is_class: boolean;
  class_available: boolean;
  class_cta_id: string | null;
  class_cta_name: string | null;
  class_promo_code: string | null;
  prepared_status: "class" | "missing" | null;
}

export interface CTAInitPreview {
  status: CTAInitPreviewStatus;
  proposal: CTAInitProposal | null;
  /** Migration 1580 (absent unless the event is a range night in a state with a voucher). */
  class_promo?: CTAClassPromoStatus | null;
  /** Migration 1360 (absent from an older server). */
  default_amount?: CTADefaultAmount | null;
  generic_cta: { id: string; has_url: boolean; has_qr: boolean } | null;
  initialization: CTAInitRecord | null;
}

export type CTAInitResult =
  | {
      status: "initialized";
      event_cta_id: string;
      parameters: CTAInitParameters;
      voucher_amount: number;
      resolution: string;
      derived_overrides: CTAInitOverride[];
      missing_parameters: string[];
    }
  | { status: "source_changed"; proposal: CTAInitProposal }
  | { status: "already_initialized" | "not_pristine"; event_cta_id?: string }
  | {
      status:
        | "not_eligible"
        | "predates_release"
        | "invalid_amount"
        | "invalid_parameters"
        | "forbidden"
        | "event_gone";
    };

export type CTADeferResult =
  | { status: "deferred" | "deferred_exists"; deferred_at?: string }
  | {
      status:
        | "already_initialized"
        | "predates_release"
        | "forbidden"
        | "event_gone";
    };

/**
 * Whether the current user may change this event's CTAs -- the SAME helper the RPCs and the
 * event_call_to_actions UPDATE policy use (admins, event_permissions owner/edit, the event's creator).
 * Deliberately NOT getUserEventAccess: that omits the creator and admits the assigned MSR (audit H2).
 */
export async function canEditEventCTAs(eventId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("eib_can_edit_event_ctas", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return data === true;
}

export async function previewEventCTADefaults(
  eventId: string,
): Promise<CTAInitPreview> {
  // Migration 1360: NULL = the event's default amount (the server's state-default rule), not a fixed $20.
  const { data, error } = await supabase.rpc("eib_preview_event_cta_defaults", {
    p_event_id: eventId,
    p_voucher_amount: null,
  });
  if (error) {
    throw error;
  }
  return data as CTAInitPreview;
}

export async function initializeEventCTA(params: {
  eventId: string;
  fingerprint: string;
  parameters: CTAInitParameters;
  voucherAmount: number;
}): Promise<CTAInitResult> {
  const { data, error } = await supabase.rpc("eib_initialize_event_cta", {
    p_event_id: params.eventId,
    p_fingerprint: params.fingerprint,
    p_parameters: params.parameters,
    p_voucher_amount: params.voucherAmount,
  });
  if (error) {
    throw error;
  }
  return data as CTAInitResult;
}

export async function deferEventCTA(eventId: string): Promise<CTADeferResult> {
  const { data, error } = await supabase.rpc("eib_defer_event_cta", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return data as CTADeferResult;
}

/** Migration 530: " Used its CTA marked for range night." when the source is marked for an activity; "" otherwise. */
function activityNote(p: CTAInitProposal): string {
  return p.source_activity_type
    ? ` Used its CTA marked for ${ctaActivityLabel(p.source_activity_type).toLowerCase()}.`
    : "";
}

function formatAmount(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/** Migration 1360: " Tracking codes only: ..." when the source is a tracking template; "" otherwise. */
function templateNote(p: CTAInitProposal): string {
  if (p.source_role !== "tracking_template") {
    return "";
  }
  const amt = p.source?.voucher_amount;
  const theirs =
    amt === null || amt === undefined
      ? "has no voucher"
      : `is for $${formatAmount(Number(amt))}`;
  return ` Its CTA ${theirs}, so only the tracking codes were copied; the voucher amount is this event's own.`;
}

/** Operator-facing sentence for how the source was (or was not) chosen. */
export function describeResolution(p: CTAInitProposal): string {
  const org = p.source_organization_name ?? "the linked organization";
  switch (p.resolution) {
    case "promoter":
      return `Copied from the promoter, ${org}.${activityNote(p)}${templateNote(p)}`;
    case "legacy_promoter_link":
      return `Copied from ${org}, the promoter linked to this event.${activityNote(p)}${templateNote(p)}`;
    case "host":
      return `Copied from the host, ${org}.${activityNote(p)}${templateNote(p)}`;
    case "state_no_reseller": {
      // Migration 1712: the promoter has nothing to copy, so the state's No Reseller codes are used.
      const f = p.no_reseller_fallback;
      const promoter = f?.promoter_organization_name ?? "The promoter";
      const how =
        f?.mode === "organization_codes"
          ? `its Affiliate ID${p.parameters.promo ? " and Promo Code" : ""}`
          : "its CTA";
      return `${promoter} has no CTA with tracking codes, so the ${f?.state ?? "state"} No Reseller codes were used (${org}, ${how}). The voucher amount is this event's own.${templateNote(p)}`;
    }
    case "no_inheritance":
      return "This event type doesn't copy tracking codes from another CTA. Fill in any you need.";
    case "source_missing":
      return `${org} has no CTA with tracking codes yet.${noResellerUnavailableNote(p)} Add a subscription CTA with an Affiliate ID and a Promo Code on ${org}'s Calls to Action tab (any voucher amount, or none) and this event is prepared automatically within a few minutes — or fill in the tracking codes yourself.`;
    case "review:no_link":
      return "No promoter or host organization is linked to this event, so there is nothing to copy. Link one on the event, or fill in the tracking codes yourself.";
    case "review:ambiguous_links":
      return "More than one organization is linked to this event, so it isn't clear whose tracking codes to use. Fill them in yourself.";
    case "review:host_is_not_a_promoter":
      return `${org} is linked to this gun show but isn't a promoter, so its codes weren't copied. Set the event's promoter, or fill them in yourself.`;
    case "review:no_cta_at_amount": {
      const have = (p.scope_amounts ?? [])
        .map((a) => (a === null ? "no amount" : `$${formatAmount(Number(a))}`))
        .join(", ");
      // Migration 1360: CTAs exist, but none at this amount and none with both an affid and a promo code to use as a template.
      return `${org} has no CTA with tracking codes yet: none of its subscription CTAs${have ? ` (${have})` : ""} has both an Affiliate ID and a Promo Code, so nothing was copied.${noResellerUnavailableNote(p)} Add them to one of ${org}'s CTAs (any voucher amount, or none) and this event is prepared automatically within a few minutes — or fill in the tracking codes yourself.`;
    }
    case "review:no_cta_for_activity": {
      const marked = (p.scope_activity_types ?? [])
        .map(ctaActivityLabel)
        .join(", ");
      const activity = ctaActivityLabel(p.event_type).toLowerCase();
      return `${org}'s $${formatAmount(Number(p.voucher_amount))} subscription CTAs are all marked for other activities${marked ? ` (${marked})` : ""}, not ${activity}, so nothing was copied. Mark one for ${activity} or "Any activity" on ${org}'s Calls to Action tab, or fill in the tracking codes yourself.`;
    }
    case "review:source_is_other_venue":
      return `${org} only has CTAs for its other locations. Copying one would credit the wrong venue, so nothing was copied.`;
    default:
      return "Nothing was copied.";
  }
}

/** Migration 1360: where the default voucher amount came from, for the amount field's hint. */
export function describeDefaultAmount(
  d: CTADefaultAmount | null | undefined,
): string {
  if (!d) {
    return "";
  }
  const amt = `$${formatAmount(Number(d.amount))}`;
  switch (d.basis) {
    case "event_setting":
      return `${amt} is set on this event.`;
    case "state_default":
      return `${amt} is the default for ${d.state ?? "this state"}.`;
    case "gun_show_cap":
      return `${amt} is the gun show amount for ${d.state ?? "this state"}.`;
    case "no_state_default":
      return `${amt}: ${d.state ?? "this event's state"} has no state default.`;
    case "state_offer":
      return `${amt} is the Event in a Box offer for ${d.state ?? "this state"}${d.gsw_kit ? " (plus a Gunshot Wound Kit)" : ""}. It is also the most this event can give: you can lower it, not raise it.`;
    default:
      return `${amt} is the standard amount.`;
  }
}

/** Operator-facing sentence for one server intervention. */
export function describeOverride(o: CTAInitOverride): string {
  if (o.key === "utm_medium" && o.action === "replaced_by_rule") {
    return `UTM Medium is always RANGENIGHT for a range night in a state with a voucher${o.source_value ? `, so the source's "${o.source_value}" wasn't used` : ""}.`;
  }
  if (o.key === "utm_term") {
    return "UTM Term is always the event's state, so the source's value wasn't used.";
  }
  if (o.key === "utm_campaign") {
    return o.reason === "source_amount_unknown"
      ? `UTM Campaign "${o.source_value ?? o.submitted_value}" wasn't copied: the source CTA has no voucher amount to confirm it matches.`
      : `UTM Campaign "${o.source_value ?? o.submitted_value}" wasn't copied: it describes a different voucher amount.`;
  }
  return `"${o.key}" isn't a supported CTA parameter and was left out.`;
}

/** Migration 1360: one upcoming gun show / range night whose event CTA is not ready. */
export interface EventAwaitingSourceCTA {
  event_id: string;
  title: string;
  type: "gun_show" | "range_night";
  start_time: string;
  state: string | null;
  /**
   * awaiting_source: not prepared (see resolution); qr_pending: prepared, QR / flyer not drawn yet;
   * manual_only: its generic CTA already has a link, QR or flyer (maybe printed), so it is never prepared automatically.
   */
  status: "awaiting_source" | "qr_pending" | "manual_only";
  resolution?: string;
  source_organization_id?: string | null;
  source_organization_name?: string | null;
  scope_amounts?: (number | null)[] | null;
  can_edit: boolean;
}

export async function listEventsAwaitingSourceCTA(): Promise<
  EventAwaitingSourceCTA[]
> {
  const { data, error } = await supabase.rpc("eib_events_awaiting_source_cta");
  if (error) {
    throw error;
  }
  const out = data as { status: string; events: EventAwaitingSourceCTA[] };
  return out?.status === "ok" ? (out.events ?? []) : [];
}

/** Short operator-facing reason for the awaiting list. */
export function describeAwaitingReason(e: EventAwaitingSourceCTA): string {
  const who =
    e.source_organization_name ??
    (e.type === "gun_show" ? "The promoter" : "The host");
  if (e.status === "qr_pending") {
    return "Prepared — open the event to finish the QR code and flyer";
  }
  if (e.status === "manual_only") {
    return "Already has a QR code, so it isn't set up automatically — prepare it by hand";
  }
  switch (e.resolution) {
    case "source_missing":
    case "review:no_cta_at_amount":
      return `${who} has no CTA with tracking codes yet`;
    case "review:no_link":
      return e.type === "gun_show"
        ? "No promoter set"
        : "No host organization linked";
    case "review:ambiguous_links":
      return "More than one organization linked";
    case "review:host_is_not_a_promoter":
      return `${who} is not a promoter`;
    case "review:no_cta_for_activity":
      return `${who}'s CTAs are marked for other activities`;
    case "review:source_is_other_venue":
      return `${who} only has CTAs for other locations`;
    default:
      return "Not prepared";
  }
}

/**
 * Migration 1580: the warning (or confirmation) for a proposal on a range night in a voucher state. null when the rule
 * does not apply (gun shows, other types, states without a voucher).
 */
export function classPromoNote(
  p: CTAInitProposal,
): { tone: "warning" | "info"; text: string } | null {
  if (!p.class_promo) {
    return null;
  }
  const org = p.source_organization_name ?? "the host organization";
  if (p.class_promo.status === "class") {
    return {
      tone: "info",
      text: `Copied from ${org}'s CLASS promo CTA${p.source?.name ? ` (${p.source.name})` : ""}. UTM Medium is set to RANGENIGHT.`,
    };
  }
  const using = p.source?.name ? `using ${p.source.name}` : "no CTA to copy";
  return {
    tone: "warning",
    text: `No CLASS promo CTA for ${org}; ${using}; promo code may be outdated.${p.class_promo.other_venue_class ? ` ${org} has a CLASS CTA only for another of its locations, which would credit that location.` : ""} Add a subscription CTA whose promo code ends in CLASS on ${org}'s Calls to Action tab.`,
  };
}

/**
 * Migration 1580: the warning for an event CTA that is already prepared, when its promo code is not a CLASS code.
 * null when the rule does not apply or the promo code is a CLASS code.
 */
export function preparedClassPromoWarning(
  c: CTAClassPromoStatus | null | undefined,
): string | null {
  if (!c || c.cta_promo_is_class) {
    return null;
  }
  const org = c.organization_name ?? "the host organization";
  const current = c.cta_promo ? `"${c.cta_promo}"` : "no promo code";
  if (c.class_available) {
    return `This CTA uses ${current}, not ${org}'s CLASS promo code "${c.class_promo_code ?? ""}". It isn't changed automatically because its link, QR code or flyer may already be in use; update the CTA's Promo Code and UTM Medium (RANGENIGHT), then regenerate the QR code and flyer.`;
  }
  return `No CLASS promo CTA for ${org}; this CTA uses ${current}; promo code may be outdated. Add a subscription CTA whose promo code ends in CLASS on ${org}'s Calls to Action tab, then update this CTA's Promo Code and UTM Medium (RANGENIGHT) and regenerate its QR code and flyer.`;
}
